import React, { useState, useMemo, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Container, Form, Row, Col } from 'react-bootstrap';
import Table from '@components/table';
import { REST_API } from '@awp/constants';
import { GetJson } from '@awp/helpers/fetchReact';
import { BOOTSTRAP_BREAKPOINTS, checkBreakpoint } from '@awp/helpers/UIHelper';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { setHours, setMinutes, subDays, isValid, isAfter, format as formatDate } from 'date-fns';
import { IconCaretDownFill, IconCaretRightFill } from '@components/awpIcons';
import { debounce, isValueEmpty, isJsonValueEmpty, getUtcToLocalByFormat } from '@awp/helpers/utilsReact';
import beautify from 'xml-beautifier';
import { AwpContainer } from '@awp/components/Common/AwpContainer';

const IngestionFailuresTable = () => {
  const [isMedium, setIsMedium] = React.useState(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM));
  const [errStartDate, setErrStartDate] = useState('');
  const [errEndDate, setErrEndDate] = useState('');
  const [apiStartDate, setApiStartDate] = useState(setMinutes(setHours(subDays(new Date(), 1), 0), 0));
  const [apiEndDate, setApiEndDate] = useState(setMinutes(setHours(new Date(), 23), 59));
  const [search, setSearch] = useState('');
  const [errordata, setErrordata] = useState(null);
  const [idx, setIdx] = useState(0);
  const rowBotoom = {
    borderBottom: '0.063rem solid #bdc5cc',
  };
  const colPadding = {
    padding: '0.75rem',
  };

  useEffect(() => {
    const handleResize = debounce(() => {
      setIsMedium(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM));
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const cellComponentExp = ({ row, rows, toggleRowExpanded }) => {
    return (
      // Use Cell to render an expander for each row.
      // We can use the getToggleRowExpandedProps prop-getter
      // to build the expander.
      <span
        {...row.getToggleRowExpandedProps({
          onClick: () => {
            const expandedRow = rows.find((row) => row.isExpanded);

            if (expandedRow) {
              const isSubItemOfRow = Boolean(expandedRow && row.id.split('.')[0] === expandedRow.id);

              if (isSubItemOfRow) {
                const expandedSubItem = expandedRow.subRows.find((subRow) => subRow.isExpanded);

                if (expandedSubItem) {
                  const isClickedOnExpandedSubItem = expandedSubItem.id === row.id;
                  if (!isClickedOnExpandedSubItem) {
                    toggleRowExpanded(expandedSubItem.id, false);
                  }
                }
              } else {
                toggleRowExpanded(expandedRow.id, false);
              }
            }
            row.toggleRowExpanded();
          },
        })}
      >
        {row.isExpanded ? <IconCaretDownFill /> : <IconCaretRightFill />}
      </span>
    );
  };

  const TableColumns = useMemo(
    () => [
      {
        // Make an expander cell
        Header: () => null, // No header
        id: 'expander', // It needs an ID
        Cell: cellComponentExp,
      },
      {
        Header: 'Issue Date',
        accessor: 'FailureTimestamp',
        sortType: sortByNumericThenAlpha,
        Cell: ({ value }) => {
          return getUtcToLocalByFormat(value, 'yyyy-MM-dd HH:mm:ss');
        },
      },
      {
        Header: 'Id',
        accessor: 'TraceId',
        show: isMedium,
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Source',
        accessor: 'SourceProviderName',
        show: isMedium,
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Failure Description',
        accessor: 'FailureDescription',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Failure Type',
        accessor: 'IngestionFailureType',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Source Content',
        accessor: 'SourceContent',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Is Lndb Queue Pending',
        accessor: 'IsLndbQueuePending',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Code Location',
        accessor: 'FailureElementName',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'KeyWords',
        accessor: 'FailureKeywordList',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'LndbQueueTimestamp',
        accessor: 'LndbQueueTimestamp',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'SourceContentType',
        accessor: 'SourceContentType',
        sortType: sortByNumericThenAlpha,
      },
    ],
    [isMedium],
  );
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  /* istanbul ignore next */
  const handleStartDateInput = (date) => {
    if (!isValid(date)) {
      setErrStartDate('Start date is invalid.');
      return;
    }
    if (!isValueEmpty(date) && !isAfter(date, apiEndDate)) {
      setApiStartDate(date);
    } else {
      setErrStartDate('Start date must be before end date.');
      return;
    }
    setErrStartDate('');
    return true;
  };
  /* istanbul ignore next */
  const handleEndDateInput = (date) => {
    if (!isValid(date)) {
      setErrEndDate('End date is invalid.');
      return;
    }
    if (!isValueEmpty(date) && !isAfter(apiStartDate, date)) {
      setApiEndDate(date);
    } else {
      setErrEndDate('Start date must be before end date.');
      return;
    }
    setErrEndDate('');
    return true;
  };
  const fetchData = useCallback(() => {
    setLoading(true);
    let startDateString = formatDate(apiStartDate, 'yyyy-MM-dd-HH-mm');
    let endDateString = formatDate(apiEndDate, 'yyyy-MM-dd-HH-mm');

    let url = REST_API.INGESTION_FAILURES;
    url += '?query[generalSearch]=' + (encodeURIComponent(search) || '');
    url += '&query[startDate]=' + (startDateString || '');
    url += '&query[endDate]=' + (endDateString || '');

    GetJson(url)
      .then((response) => response.json())
      .then((data) => {
        setData(data);
        setLoading(false);
      });
  }, [search, apiStartDate, apiEndDate]);

  const fetchIngestFailure = useCallback((id) => {
    if (!id) return;

    let url = REST_API.INGESTION_FAILURE;
    url += `${id}`;

    GetJson(url)
      .then((response) => response.json())
      .then((data) => {
        setErrordata(data);
      });
  });

  const getFirstMatchedKeyword = (text, keywords) => {
    let firstKey = '';
    let firstIndex = text.length;
    keywords.forEach((keyword) => {
      let i = text.toLowerCase().indexOf(keyword.toLowerCase());
      if (i > -1 && i < firstIndex) {
        firstIndex = i;
        firstKey = keyword;
      }
    });
    return [firstKey, firstIndex];
  };

  const isJsonString = (str) => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  const getHighlightedText = (txtString, keywords) => {
    // Split text on highlight term, include term itself into jsonKeyWordList, ignore case
    if (isValueEmpty(txtString)) return '';
    let formatedTxtString = isJsonString(txtString)
      ? JSON.stringify(JSON.parse(txtString), null, 2)
      : beautify(txtString);
    let jsonKeyWordList = isJsonString(keywords) ? JSON.parse(keywords) : null;
    if (isJsonValueEmpty(jsonKeyWordList)) {
      return (
        <div className="overflow-auto source-content" style={{ maxHeight: '12.5rem', whiteSpace: 'pre' }}>
          {formatedTxtString}
        </div>
      );
    }
    let formatedTxtStrCopy = formatedTxtString;
    const list = [];
    let i = 0;
    while (formatedTxtStrCopy) {
      let [keyword, keywordIndex] = getFirstMatchedKeyword(formatedTxtStrCopy, jsonKeyWordList);
      if (isValueEmpty(keyword)) {
        if (i == 0) {
          return (
            <div className="overflow-auto source-content" style={{ maxHeight: '12.5rem', whiteSpace: 'pre' }}>
              {formatedTxtString}
            </div>
          );
        }
        if (i > 0) list.push(formatedTxtStrCopy);
        break;
      }
      i++;
      const text = formatedTxtStrCopy.split(keyword, 1)[0];
      list.push(text);
      list.push(keyword);
      formatedTxtStrCopy = formatedTxtStrCopy.substring(keywordIndex + keyword.length);
    }

    return (
      <div className="overflow-auto source-content" style={{ maxHeight: '12.5rem', whiteSpace: 'pre' }}>
        {list.map((elem, index) =>
          index % 2 === 0 ? (
            elem
          ) : (
            <span style={{ color: 'red', maxWidth: 'unset' }} key={`span_swobml_highlight_key_${index}`}>
              {elem}
            </span>
          ),
        )}
      </div>
    );
  };
  const convertCollectionUTCStringToLocal = (paramString) => {
    const utcRegex = /collection time \(([\d-]+ [\d:]+ [A|P]M)\)/;
    const match = paramString.match(utcRegex);

    if (!match) {
      // If the substring is not found, return the original parameter string.
      return paramString;
    }

    let utcDateTime = match[1];
    const [dateString, timeString] = utcDateTime.split(' ');
    const [year, month, day] = dateString.split('-');
    const [hourString, minuteString, secondString] = timeString.split(':');
    let hour = parseInt(hourString, 10);
    if (utcDateTime.endsWith('PM') && hour < 12) {
      hour += 12;
    } else if (utcDateTime.endsWith('AM') && hour === 12) {
      hour = 0;
    }
    const minute = parseInt(minuteString, 10);
    const second = parseInt(secondString, 10);
    const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    const utcDateTimeISO = utcDate.toISOString().replace('.000Z', 'Z');
    const localDateTime = getUtcToLocalByFormat(utcDateTimeISO);
    return paramString.replace(`(${utcDateTime})`, `(${localDateTime})`);
  };

  const renderRowSubComponent = useCallback(
    ({ row }) => {
      if (!row || !row.original) return;

      if (errordata !== null && idx === 2 && errordata.Id !== row.original.Id) {
        //reset
        setIdx(0);
        setErrordata(null);
        return;
      }
      if (row.original.Id > 0 && errordata == null && idx === 0) {
        //fetch failure log
        fetchIngestFailure(row.original.Id);
        setIdx(1);
        return;
      }
      if (row.original.Id > 0 && errordata !== null && idx === 1) {
        setIdx(idx + 1);
      }
      return (
        <Container style={{ fontSize: '13px' }}>
          <Row style={rowBotoom}>
            <Col md={3} style={colPadding}>
              <span style={{ fontWeight: 'bold' }}>Code Location</span>
            </Col>
            <Col md={9} style={colPadding}>
              {errordata ? errordata.FailureElementName : ''}
            </Col>
          </Row>
          <Row style={rowBotoom}>
            <Col md={3} style={colPadding}>
              <span style={{ fontWeight: 'bold' }}>Description</span>
            </Col>
            <Col md={9} style={colPadding}>
              <div className="overflow-auto source-content" style={{ maxHeight: '12.5rem' }}>
                {errordata ? errordata.FailureDescription : ''}
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={3} style={colPadding}>
              <span style={{ fontWeight: 'bold' }}>Source Content</span>
            </Col>
            <Col md={9} style={colPadding}>
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '2px' }}>
                  Type: {errordata ? errordata.SourceContentType : ''}
                </div>
                {errordata ? getHighlightedText(errordata.SourceContent, errordata.FailureKeywordList) : ''}
              </>
            </Col>
          </Row>
        </Container>
      );
    },
    [errordata, idx],
  );

  const handldeKeyUp = (e) => {
    e.persist();
    //** DO NOT USE debounce */
    setTimeout(() => {
      if (e.target != null) setSearch(e.target.value);
    }, 4000);
  };
  return (
    <AwpContainer>
      <div className="d-flex">
        <h1 className="flex-grow-1 align-middle text-dark mb-0" style={{ lineHeight: '3rem' }}>
          Data Ingestion Issue Log
        </h1>
      </div>
      <div className="container mb-3">
        <Row style={{ marginBottom: '10px', marginTop: '10px' }}>
          <Col md={3} className="mb-3 max-pr-5">
            <Form.Label className="issue-log-label">Start Date</Form.Label>
            <DatePicker
              id="start_date_input_id"
              className="form-control"
              wrapperClassName="awp-date-picker"
              name="start_date_input"
              dateFormat={['yyyy-MM-dd HH:mm', 'yyyy-MM-dd', 'yyyyMMdd', 'yyyyMMddHHmm']}
              showTimeSelect
              closeOnSelect={true}
              timeFormat="HH:mm"
              timeIntervals={60}
              disabled={loading}
              selected={apiStartDate}
              onChange={(date) => {
                handleStartDateInput(date);
              }}
            />
          </Col>
          <Col md={3} className="mb-3 max-pl-5">
            <Form.Label className="issue-log-label">End Date</Form.Label>
            <DatePicker
              id="end_date_input_id"
              className="form-control"
              wrapperClassName="awp-date-picker"
              name="end_date_input"
              dateFormat={['yyyy-MM-dd HH:mm', 'yyyy-MM-dd', 'yyyyMMdd', 'yyyyMMddHHmm']}
              showTimeSelect
              closeOnSelect={true}
              timeFormat="HH:mm"
              timeIntervals={60}
              disabled={loading}
              selected={apiEndDate}
              onChange={(date) => {
                handleEndDateInput(date);
              }}
            />
          </Col>
          <Col md={4} className="mb-3">
            <Form.Label className="issue-log-label">Search Log</Form.Label>
            <Form.Control
              type="text"
              id="ilGeneralSearch"
              onKeyUp={(e) => {
                handldeKeyUp(e);
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Form.Control.Feedback type="invalid" className={errStartDate ? 'd-block' : 'd-none'}>
              {errStartDate}
            </Form.Control.Feedback>
          </Col>
          <Col md={4}>
            <Form.Control.Feedback type="invalid" className={errEndDate ? 'd-block' : 'd-none'}>
              {errEndDate}
            </Form.Control.Feedback>
          </Col>
          <Col md={4}></Col>
        </Row>
      </div>
      <div className="container mb-3">
        <Table
          columns={TableColumns}
          data={data}
          fetchData={fetchData}
          loading={loading}
          search={search}
          filters={[apiStartDate, apiEndDate]}
          dataTriggers={[]}
          defaultSortBy={'FailureTimestamp'}
          defaultDesc={true}
          renderRowSubComponent={renderRowSubComponent}
          hiddenColumns={[
            'Id',
            'SourceContent',
            'SourceContentType',
            'IsLndbQueuePending',
            'FailureDescription',
            'FailureElementName',
            'FailureKeywordList',
            'LndbQueueTimestamp',
          ]}
          expendRowStyle={{ backgroundColor: '#f7f8fa' }}
        />
      </div>
    </AwpContainer>
  );
};

export default IngestionFailuresTable;
